"use client"
import { allowedOrigins } from '@/lib/constants';
import { getAuthTokenToFront } from '@/lib/data/auth';
import { clearCartOnComplete, getOrSetCart, setCart } from '@/lib/data/cart';
import { HttpTypes } from "@medusajs/types";
import { useParams, useRouter } from 'next/navigation';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

const isDev = process.env.NODE_ENV === "development"

interface MessageData {
  type: string;
  message: string;
  timestamp: string;
  data: HttpTypes.StoreCart;
}

// Create a context for the iframe eventData
const IframeContext = createContext<{
  eventData: MessageData | null;
  setEventData: React.Dispatch<React.SetStateAction<MessageData | null>>;
  sentLogoutDataToIframe: () => void;
  cartNeedRefresh: boolean
  handleCartNeedRefresh?: () => void
  sentRefreshDataToIframe?: () => void
}>({
  eventData: null,
  setEventData: () => { },
  sentLogoutDataToIframe: () => { },
  cartNeedRefresh: false
});

interface IframeProviderProps {
  children: ReactNode;
  currentCartId: string;
  locale: string;
}

// Create the Iframe context provider
export const IframeProvider: React.FC<IframeProviderProps> = ({ children, currentCartId, locale }) => {
  const [eventData, setEventData] = useState<MessageData | null>(null);
  let [cartNeedRefresh, setCartNeedRefresh] = useState(false)
  const router = useRouter()
  const setOrUpdateCart = async (iframeCart: HttpTypes.StoreCart) => {
    if (currentCartId === iframeCart.id) {
      await setCart(locale, iframeCart.id)
      router.push(`/checkout`)
    } else {
      await setCart(locale, iframeCart.id)
      router.push(`/checkout`)
    }
  }
  useEffect(() => {

    const iframeOperation = async () => {
      const iFrame = document.getElementById('AiFrame') as HTMLIFrameElement | null;
      if (iFrame) {
        let cart = await getOrSetCart(locale)
        let token = await getAuthTokenToFront()
        // 初始化cart_id
        sendDataToIframe({
          type: "init_cart",
          data: {
            cartId: cart.id,
            token: token,
            auth_type: "medusa-auth"
          }
        }, "sendCartIdToIframe")

        if (localStorage.getItem('retrySendLogoutData') === 'true') {
          sentLogoutDataToIframe();
        }
        if (localStorage.getItem('retrySendRefresh') === 'true') {
          sentRefreshDataToIframe();
        }
      }
    }

    const IFRAME_MESSAGE_TYPES = new Set([
      "cart_data",
      "cart_refresh",
      "ready",
      "view_product",
      "cart_completed",
      "need_login",
    ])

    const handleIframeMessage = (event: MessageEvent) => {
      const data = event.data
      const messageType =
        data && typeof data === "object" && typeof data.type === "string"
          ? data.type
          : null

      // Browser extensions / Tolgee / unrelated postMessage spam — ignore quietly
      if (!messageType || !IFRAME_MESSAGE_TYPES.has(messageType)) {
        return
      }

      if (!allowedOrigins.includes(event.origin)) {
        if (isDev) {
          console.warn("Invalid origin for iframe message:", event.origin, messageType)
        }
        if (!isDev) {
          return
        }
      }

      if (data.type === "cart_data" && data.data) {
        setEventData(data)
        setOrUpdateCart(data.data)
      }
      if (data.type === "cart_refresh") {
        setCartNeedRefresh(true)
      }
      if (data.type === "ready") {
        iframeOperation()
      }

      if (data.type === "view_product") {
        if (data.data.source === "fbe") {
          location.href = `/products/${data.data.handle}`
        }
      }
      if (data.type === "cart_completed") {
        const completeCart = async () => {
          await clearCartOnComplete()
          await getOrSetCart(locale)
          await iframeOperation()
          setCartNeedRefresh(true)
        }
        completeCart()
      }

      if (data.type === "need_login") {
        router.push(`${locale}/user`)
      }
    }

    // 如果iframe比父站点先加载完成，只使用message event会出现还未开始监听event就已经到来，会丢失event
    iframeOperation()

    // Attach the eventData listener to window
    window.addEventListener('message', handleIframeMessage);

    // Cleanup the listener on component unmount

    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };

  }, []);

  const sendDataToIframe = (message: any, localStorageFallback: string) => {
    const iFrame = document.getElementById('AiFrame') as HTMLIFrameElement | null;
    if (iFrame) {
      const iframeOrigin = new URL(iFrame.src).origin;
      const isValidOrigin = allowedOrigins.includes(iframeOrigin);

      if (isValidOrigin || isDev) {
        iFrame.contentWindow?.postMessage(message, iframeOrigin);
      } else {
        console.warn('Invalid iframe origin:', iframeOrigin);
      }

      // Remove the retry flag from localStorage once the iframe is found
      localStorage.removeItem(localStorageFallback);
    } else {
      if (isDev) {
        console.warn('Iframe element not found.');
      }
      // Save flag to localStorage to retry when iframe becomes available
      localStorage.setItem(localStorageFallback, 'true');
    }
  }

  const sentLogoutDataToIframe = () => {
    const message = {
      type: 'logout',
      message: 'Reset data user logged out and cart cleared',
    };
    sendDataToIframe(message, "retrySendLogoutData")
  };

  const sentRefreshDataToIframe = () => {
    setCartNeedRefresh(true)
    sendDataToIframe({
      type: "cart_refresh"
    }, "retrySendRefresh")
  }

  const handleCartNeedRefresh = () => {
    if (cartNeedRefresh) {
      setCartNeedRefresh(false)
      return true;
    }
  }

  return (
    <IframeContext.Provider value={{ eventData, setEventData, sentLogoutDataToIframe, handleCartNeedRefresh, cartNeedRefresh, sentRefreshDataToIframe }}>
      {children}
    </IframeContext.Provider>
  );
};

// Custom hook to use the Iframe context
export const useIframeEvent = () => {
  const context = useContext(IframeContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};